import React, { useEffect, useState } from 'react';
import { useTheme } from '../../hooks/useTheme';
import { COLORS, FONTS } from '../../styles/tokens';

export type ConfirmVariant = 'primary' | 'danger';

interface ConfirmationModalProps {
  open: boolean;
  title: string;
  body: React.ReactNode;
  confirmLabel: string;
  cancelLabel?: string;
  variant?: ConfirmVariant;
  /** When set, the user must type this exact string to enable the confirm button. */
  requiredText?: string;
  onConfirm: () => void | Promise<void>;
  onCancel: () => void;
}

const VARIANT_ACCENTS: Record<ConfirmVariant, string> = {
  primary: '#01A9ED',
  danger: '#EF4444',
};

/**
 * Reusable confirmation dialog. Controlled by `open` prop; renders nothing
 * when closed so it doesn't interfere with focus or layout.
 *
 * For destructive actions, pass `requiredText` (typically the app slug) —
 * the confirm button stays disabled until the user types it exactly. That
 * protects against accidental clicks on delete.
 */
export function ConfirmationModal(props: ConfirmationModalProps): React.JSX.Element | null {
  const { isDark } = useTheme();
  const colors = isDark ? COLORS.dark : COLORS.light;
  const [typed, setTyped] = useState('');
  const [busy, setBusy] = useState(false);
  const variant: ConfirmVariant = props.variant ?? 'primary';
  const accent = VARIANT_ACCENTS[variant];
  const requirementMet = !props.requiredText || typed === props.requiredText;
  const canConfirm = requirementMet && !busy;

  useEffect(() => {
    if (!props.open) { setTyped(''); setBusy(false); }
  }, [props.open]);

  if (!props.open) return null;

  const handleConfirm = async (): Promise<void> => {
    if (!canConfirm) return;
    try {
      setBusy(true);
      await props.onConfirm();
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="confirmation-title"
      onKeyDown={e => { if (e.key === 'Escape' && !busy) props.onCancel(); }}
      style={backdropStyle()}
    >
      <div className="vx-modal-fit" style={cardStyle(isDark, accent)}>
        <h2 id="confirmation-title" style={titleStyle(accent)}>{props.title}</h2>
        <div style={bodyStyle(colors.text)}>{props.body}</div>

        {props.requiredText && (
          <RequiredInput
            requiredText={props.requiredText}
            value={typed}
            colors={colors}
            isDark={isDark}
            accent={accent}
            onChange={setTyped}
          />
        )}

        <div style={actionsStyle()}>
          <Button onClick={props.onCancel} disabled={busy} variant="ghost" colors={colors}>
            {props.cancelLabel ?? 'Cancel'}
          </Button>
          <Button onClick={handleConfirm} disabled={!canConfirm} variant={variant} accent={accent}>
            {busy ? 'Working…' : props.confirmLabel}
          </Button>
        </div>
      </div>
    </div>
  );
}

/** Full-screen translucent backdrop used to center the modal card. */
function backdropStyle(): React.CSSProperties {
  return {
    position: 'fixed', inset: 0, zIndex: 10000,
    background: 'rgba(0,0,0,0.55)',
    display: 'grid', placeItems: 'center',
    padding: '24px',
  };
}

/** Card style — accent colored border keys variant. */
function cardStyle(isDark: boolean, accent: string): React.CSSProperties {
  return {
    width: '100%', maxWidth: '480px',
    padding: '24px',
    borderRadius: '14px',
    backgroundColor: isDark ? COLORS.dark.card : COLORS.light.card,
    border: `1px solid ${accent}55`,
    boxShadow: '0 24px 48px rgba(0,0,0,0.42)',
  };
}

function titleStyle(accent: string): React.CSSProperties {
  return {
    margin: 0,
    fontFamily: FONTS.heading,
    fontSize: '13px',
    letterSpacing: '0.18em',
    textTransform: 'uppercase',
    color: accent,
  };
}

function bodyStyle(textColor: string): React.CSSProperties {
  return {
    fontFamily: FONTS.body,
    fontSize: '13px',
    color: textColor,
    lineHeight: '1.5',
    marginTop: '14px',
    marginBottom: '18px',
    whiteSpace: 'pre-wrap',
  };
}

function actionsStyle(): React.CSSProperties {
  return {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '10px',
    marginTop: '20px',
  };
}

interface ButtonProps {
  onClick: () => void;
  disabled?: boolean;
  variant: 'primary' | 'danger' | 'ghost';
  accent?: string;
  colors?: { text: string; textSecondary: string; border: string };
  children: React.ReactNode;
}

/** Shared modal button — gradient for primary, solid for danger, outlined for ghost. */
function Button(props: ButtonProps): React.JSX.Element {
  const style: React.CSSProperties = {
    padding: '10px 18px',
    borderRadius: '8px',
    border: props.variant === 'ghost' ? `1px solid ${props.colors?.border ?? 'rgba(255,255,255,0.15)'}` : 'none',
    background: props.variant === 'ghost'
      ? 'transparent'
      : props.variant === 'danger'
        ? '#EF4444'
        : 'linear-gradient(90deg, #01A9ED, #0165C6)',
    color: props.variant === 'ghost' ? (props.colors?.text ?? '#FFF') : '#FFF',
    fontFamily: FONTS.heading,
    fontSize: '11px',
    letterSpacing: '0.15em',
    textTransform: 'uppercase',
    cursor: props.disabled ? 'not-allowed' : 'pointer',
    opacity: props.disabled ? 0.55 : 1,
  };
  return (
    <button onClick={props.onClick} disabled={props.disabled} style={style}>
      {props.children}
    </button>
  );
}

/** Typed-confirmation input used for destructive operations. */
function RequiredInput(props: {
  requiredText: string;
  value: string;
  colors: { text: string; textSecondary: string; border: string; card: string };
  isDark: boolean;
  accent: string;
  onChange: (v: string) => void;
}): React.JSX.Element {
  const matches = props.value === props.requiredText;
  return (
    <div style={{ marginBottom: '10px' }}>
      <div style={{ fontFamily: FONTS.body, fontSize: '11px', color: props.colors.textSecondary, marginBottom: '6px' }}>
        Type <code style={{ color: props.accent }}>{props.requiredText}</code> to confirm.
      </div>
      <input
        value={props.value}
        onChange={e => props.onChange(e.target.value)}
        placeholder={props.requiredText}
        style={{
          width: '100%', height: '36px', padding: '0 10px',
          borderRadius: '6px',
          border: `1px solid ${matches ? props.accent : props.colors.border}`,
          background: props.isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
          color: props.colors.text,
          fontFamily: 'monospace', fontSize: '13px', outline: 'none',
        }}
      />
    </div>
  );
}
